import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Message, Spin } from '@fx-ui/fine-design';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { dataURLtoBlob, get, getApi, isEmpty, uploadBlob, validExt } from '~/utils';
import Intl from '@fx-ui/intl';
import { API, COLOR_VAR } from '~/constants';
import { useStores } from '~/hooks';
import { IFileBaseInfo } from '~/interfaces';

interface IAvatarUploadDialogProps {
    title?: string;
    button?: string;
    suggestSizeTip?: string;
    aspectRatio?: number;
    visible?: boolean;
    avatar?: string;
    uploadMaxSize?: number;
    onSaveSuccess?: (image_file: IFileBaseInfo, url: string) => void;
    onSaveFail?: () => void;
    onClose?: () => void;
    width?: number; // 可自定义img图片宽
    height?: number; // 可自定义img图片高
}

const URL = window.URL || window.webkitURL;

const AvatarUploadDialog: React.FunctionComponent<IAvatarUploadDialogProps> = (
    props: IAvatarUploadDialogProps
) => {
    const {
        visible,
        avatar,
        uploadMaxSize = 20480000,
        title = Intl.get('avatar.modify'),
        button = Intl.get('avatar.save'),
        width = 200,
        height = 200,
        suggestSizeTip = Intl.get('avatar.upload.maxLimit'),
        aspectRatio = 1,
        onSaveSuccess,
        onSaveFail,
        onClose
    } = props;
    const [imgUrl, setImgUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const inputRef = useRef<any>();
    const cropperRef = useRef<any>();
    const { corpUserStore } = useStores();
    useEffect(() => {
        if (visible) {
            setLoading(false);
            setImgUrl(avatar || '');
        }
    }, [visible]);
    const renderContent = () => {
        return (
            <>
                <div className="upload-tip">{suggestSizeTip}</div>
                <div className="image-wrapper">
                    {loading && <Spin maskType="light" description={Intl.get('avatar.save.tip')} />}
                    <Cropper
                        ref={cropperRef}
                        src={imgUrl}
                        style={{
                            width: 400,
                            height: 300,
                            display: loading ? 'none' : 'block'
                        }}
                        dragMode="none"
                        aspectRatio={aspectRatio} // 长宽比
                        autoCropArea={1} // 默认截取空间比例
                        viewMode={1}
                        minCropBoxWidth={70}
                        minCropBoxHeight={70}
                        zoomable={false} // 不允许缩放
                        checkOrientation={false}
                    />
                </div>
            </>
        );
    };
    const changeImg = () => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    };
    const imgChange = (e: any) => {
        const files = e.target.files;
        if (!isEmpty(files)) {
            const file = files[0];
            if (file.size > uploadMaxSize) {
                Message.add({
                    messageType: 'toast',
                    message: Intl.get('avatar.upload.fail'),
                    type: 'warning'
                });
                e.target.value = '';
                return;
            } else if (!validExt(file, ['image/jpeg', 'image/jpg', 'image/png'])) {
                Message.add({
                    messageType: 'toast',
                    message: Intl.get('upload.notSupportFileTip'),
                    type: 'warning'
                });
                e.target.value = '';
                return;
            }
            if (imgUrl) {
                URL.revokeObjectURL(imgUrl);
            }
            const url = URL.createObjectURL(file);
            setImgUrl(url);
        }
    };
    const saveAvatar = () => {
        if (cropperRef.current?.cropper) {
            const canvas = cropperRef.current.cropper.getCroppedCanvas({
                fillColor: COLOR_VAR.bgContainer,
                width: width,
                height: height
            });
            if (!canvas) {
                return;
            }
            if (canvas.width === 0) {
                Message.add({
                    messageType: 'toast',
                    message: Intl.get('avatar.crop.fail'),
                    type: 'warning'
                });
                return;
            }
            setLoading(true);
            const blob = dataURLtoBlob(canvas.toDataURL('image/jpeg'));
            uploadBlob({
                tokenUrl: getApi(API.file.get_media_token),
                uploadData: { 'x:uploader': corpUserStore.memberId! },
                fileBlob: blob
            })
                .then((res) => {
                    const { key, bucket, mime, name, size, url } = get(res, 'data.file');
                    const image_file = { key, bucket, mime, name, size };

                    onSaveSuccess && onSaveSuccess(image_file, url);
                })
                .catch(() => {
                    setLoading(false);
                    onSaveFail && onSaveFail();
                });
        }
    };
    return (
        <>
            <input
                ref={inputRef}
                onChange={imgChange}
                style={{ display: 'none' }}
                type="file"
                accept="image/jpg,image/jpeg,image/png"
            />
            <Dialog
                className="fx-avatar-upload-dialog"
                width={440}
                height={464}
                visible={visible}
                title={title}
                separator={{ footer: false }}
                contentPadding={false}
                text4Ok={button}
                text4Cancel={Intl.get('avatar.image.change')}
                onOk={saveAvatar}
                onCancel={(_e, effect) => {
                    if (effect === 'cancel') {
                        changeImg();
                    }
                    if (effect === 'close') {
                        onClose?.();
                    }
                }}
                props4Cancel={{ disabled: loading, type: 'negative' }}
                props4Ok={{ disabled: loading, type: 'primary' }}
            >
                {renderContent()}
            </Dialog>
        </>
    );
};

export default AvatarUploadDialog;
